import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import { useCentralizedCorporateNavigation } from '@/shared/components/AdmindashboardView/hooks/use-centralized-corporate-navigation';

// import { useFeatureFlag } from '@crehana/feature-flags/react';

// import { FEATURE_FLAGS_V5 } from '@/shared/components/AdmindashboardView/featureFlags';

import {
  CORP_LOWER_MENU_ROUTES,
  CORP_ROUTES,
  INDUCTION,
  STATISTICS,
} from '../../routes';
import GET_ORGANIZATION_CONFIGURATION from './graphql/AdminDashboardOrganizationConfiguration.b2b.graphql';
import type {
  AdminDashboardOrganizationConfiguration,
  AdminDashboardOrganizationConfigurationVariables,
} from './graphql/types/AdminDashboardOrganizationConfiguration';

export const useConfiguration = () => {
  const { query, pathname, replace } = useRouter();
  const { slug } = query as { slug: string };

  const {
    isCentralizedCorporation,
    isLoading: isLoadingCentralizedCorporateNavigation,
  } = useCentralizedCorporateNavigation(slug);

  const { data, loading } = useQuery<
    AdminDashboardOrganizationConfiguration,
    AdminDashboardOrganizationConfigurationVariables
  >(GET_ORGANIZATION_CONFIGURATION, {
    variables: { slug },
    context: { clientName: 'b2b' },
    skip: !slug || isLoadingCentralizedCorporateNavigation,
    onCompleted({ organization }) {
      // We need to skip /corp/ redirections as centralizes corporations are
      // complete organizations with their own admin panel.
      if (isCentralizedCorporation) {
        return;
      }

      const isCorpRoute =
        CORP_ROUTES.findIndex(r => pathname.startsWith(r.path)) !== -1 ||
        CORP_LOWER_MENU_ROUTES.findIndex(r => pathname.startsWith(r.path)) !==
          -1 ||
        pathname.startsWith('/org/[slug]/panel/corp/busqueda') ||
        pathname.startsWith('/org/[slug]/panel/corp/configuracion');

      const canUseInduction = organization?.configuration?.canUseInduction;
      const isInductionRoute = pathname.startsWith(INDUCTION.path);

      const isLegacyLearningCorporation = organization?.isCorporation || false;

      if (isLegacyLearningCorporation && !isCorpRoute) {
        replace({ pathname: CORP_ROUTES[0].path, query: { slug } });
      } else if (
        !isLegacyLearningCorporation &&
        (isCorpRoute || (isInductionRoute && !canUseInduction))
      ) {
        replace({
          pathname: canUseInduction
            ? INDUCTION.nested[0].path
            : STATISTICS.nested[0].path,
          query: { slug },
        });
      }
    },
  });

  const isCorporation =
    isCentralizedCorporation || data?.organization?.isCorporation || false;

  return {
    data,
    isCorporation, // Updated to use new logic
    originalId: data?.organization?.originalId || 0,
    name: data?.organization?.name || '',
    configuration: data?.organization?.configuration,
    logoThumbnail: data?.organization?.logoThumbnail,
    hasProductDemoTest:
      data?.organization?.demoConfig?.hasProductDemoTest || false,
    isLoadingConfig: loading,
    slug,
    canUseInduction:
      data?.organization?.configuration?.canUseInduction || false,
    canUseIntegrations:
      data?.organization?.configuration?.canUseIntegrations || false,
    canUseElevate:
      data?.organization?.configuration?.hasStudioAvailable || false,
    casUseGamification:
      data?.organization?.configuration?.hasGamificationAvailable || false,
    canUseLiveSessions:
      data?.organization?.configuration?.hasLiveSessionAvailable || false,
  };
};

export default useConfiguration;
